/**
 * SpreadsheetPreviewStep — Step 2 of the SpreadsheetConnectDialog.
 *
 * Renders:
 *   - File summary header (badge + name + sheet count + auto-sync badge
 *     for cloud sources).
 *   - Yellow warnings banner (non-fatal: duplicate column rename, …).
 *   - Sheet checkbox list + "Seleziona/Deseleziona tutti" toggle.
 *   - Sample-rows table for the first selected sheet (collapsible).
 *   - Connector-name input.
 *   - Error banner + Indietro / Importa CTAs.
 *
 * All state lives in the parent dialog — this component is purely
 * presentational + delegates user actions back via callbacks.
 */

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { AlertTriangle, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import * as React from "react";

import { type BaseConnection, Button, type CapabilityWizardSpec, Checkbox, cn } from "@ando/ui";

import type { SpreadsheetPreviewResponse, SpreadsheetPreviewSheet } from "../../api/spreadsheetApi";
import type { ChunkedUploadProgress } from "../../hooks/useChunkedSpreadsheetUpload";
import type { SpreadsheetSourceMode } from "../../wizards/spreadsheetSpec";
import { InlineErrorBanner } from "../InlineErrorBanner";
import { VendorBrandBadge } from "../VendorBrandBadge";
import { formatBytes, renderCell, stableKey } from "./types";

export interface SpreadsheetPreviewStepProps {
  readonly spec: CapabilityWizardSpec<BaseConnection>;
  readonly sourceMode: SpreadsheetSourceMode;
  readonly preview: SpreadsheetPreviewResponse;
  readonly selectedSheets: readonly string[];
  readonly allSelected: boolean;
  readonly connectorName: string;
  readonly fileName: string;
  readonly fileSize: number | undefined;
  readonly isCloud: boolean;
  readonly error: string | null;
  readonly submitting: boolean;
  /** Progress dell'upload chunked — non null mentre i chunk volano
   *  verso MinIO. ``null`` per il path multipart classico (file < 25 MB)
   *  o quando l'upload non è in volo. */
  readonly uploadProgress: ChunkedUploadProgress | null;
  /** Cancel callback per interrompere l'upload chunked + cleanup MinIO. */
  readonly onCancelUpload: () => void;
  readonly onConnectorNameChange: (name: string) => void;
  readonly onToggleSheet: (name: string) => void;
  readonly onToggleAll: () => void;
  readonly onBack: () => void;
  readonly onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export const SpreadsheetPreviewStep: React.FC<SpreadsheetPreviewStepProps> = ({
  spec,
  sourceMode,
  preview,
  selectedSheets,
  allSelected,
  connectorName,
  fileName,
  fileSize,
  isCloud,
  error,
  submitting,
  uploadProgress,
  onCancelUpload,
  onConnectorNameChange,
  onToggleSheet,
  onToggleAll,
  onBack,
  onSubmit,
}) => {
  const { t } = useLingui();
  const totalSheets = preview.sheets.length;
  const firstSelected = React.useMemo<SpreadsheetPreviewSheet | null>(() => {
    if (selectedSheets.length === 0) return null;
    const selectedSet = new Set(selectedSheets);
    return preview.sheets.find((s) => selectedSet.has(s.original_name)) ?? null;
  }, [preview, selectedSheets]);

  const effectiveFileSize = fileSize !== undefined ? fileSize : preview.file_size_bytes;

  return (
    <form onSubmit={onSubmit} className="mt-4 flex flex-col gap-3" aria-busy={submitting}>
      {/* File summary */}
      <div className="flex items-start gap-3 rounded-md border border-hairline bg-white/60 px-3 py-2.5">
        <VendorBrandBadge vendorId={sourceMode} fallbackIcon={spec.icon} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-[13px] text-ink-1">{fileName || t`Foglio`}</p>
          <p className="mt-0.5 text-[11.5px] text-ink-3">
            <Plural value={totalSheets} one="# foglio rilevato" other="# fogli rilevati" />
            {effectiveFileSize !== undefined && effectiveFileSize > 0
              ? ` · ${formatBytes(effectiveFileSize)}`
              : ""}
          </p>
        </div>
        {isCloud ? (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-ando-orange/30 bg-ando-orange-soft px-2 py-0.5 font-medium text-[10.5px] text-ando-orange"
            title={t`Il file viene risincronizzato circa ogni 60 minuti`}
            data-testid="auto-sync-badge"
          >
            <RefreshCw className="h-2.5 w-2.5" aria-hidden />
            <Trans>Sync automatico ogni 60 min</Trans>
          </span>
        ) : null}
      </div>

      {/* Warnings banner — yellow, NOT red (these are non-fatal). */}
      {preview.warnings.length > 0 ? (
        <output
          aria-live="polite"
          className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/[0.08] px-3 py-2 text-[12px] text-ink-1"
          data-testid="preview-warnings"
        >
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 translate-y-px text-warning" aria-hidden />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="font-medium text-[12px] text-warning">
              <Trans>Avvisi</Trans>
            </span>
            {preview.warnings.map((w) => (
              <span key={w} className="text-[11.5px] text-ink-2">
                {w}
              </span>
            ))}
          </div>
        </output>
      ) : null}

      {/* Sheet checkbox list */}
      <fieldset>
        <div className="flex items-center justify-between">
          <legend className="font-medium text-[11.5px] text-ink-2">
            <Trans>Fogli rilevati</Trans>
          </legend>
          <button
            type="button"
            onClick={onToggleAll}
            className="rounded font-medium text-[11.5px] text-ando-orange hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1"
            data-testid="toggle-all-sheets"
          >
            {allSelected ? t`Deseleziona tutti` : t`Seleziona tutti`}
          </button>
        </div>
        <ul className="mt-1.5 flex flex-col gap-1.5">
          {preview.sheets.map((sheet) => {
            const isSelected = selectedSheets.includes(sheet.original_name);
            const checkboxId = `sheet-${sheet.original_name.replace(/\s+/g, "-")}`;
            return (
              <li key={sheet.original_name}>
                <label
                  htmlFor={checkboxId}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 transition-colors",
                    isSelected
                      ? "border-ando-orange/60 bg-ando-orange-soft/30"
                      : "border-hairline bg-white/50 hover:border-ando-orange/30",
                  )}
                >
                  <Checkbox
                    id={checkboxId}
                    checked={isSelected}
                    onChange={() => onToggleSheet(sheet.original_name)}
                    data-testid={`sheet-checkbox-${sheet.original_name}`}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-ink-1">
                    {sheet.original_name}
                  </span>
                  <span className="shrink-0 rounded-full border border-hairline bg-cream px-1.5 py-0.5 text-[10.5px] text-ink-3">
                    {sheet.total_rows.toLocaleString("it-IT")}{" "}
                    {sheet.total_rows === 1 ? t`riga` : t`righe`}
                  </span>
                  <span className="shrink-0 rounded-full border border-hairline bg-cream px-1.5 py-0.5 text-[10.5px] text-ink-3">
                    {sheet.columns.length} {sheet.columns.length === 1 ? t`colonna` : t`colonne`}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>

      {/* Sample-rows table for the first selected sheet (Anteprima). */}
      {firstSelected ? (
        <details
          className="rounded-md border border-hairline bg-white/40 px-3 py-2 text-[12px] text-ink-2"
          open
        >
          <summary className="cursor-pointer font-medium text-[11.5px] text-ink-2">
            <Trans>
              Anteprima · {firstSelected.original_name} · prime {firstSelected.preview_rows.length}{" "}
              {firstSelected.preview_rows.length === 1 ? t`riga` : t`righe`}
            </Trans>
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table
              className="w-full border-collapse text-[11px]"
              aria-label={t`Anteprima del foglio ${firstSelected.original_name}`}
            >
              <thead>
                <tr className="border-hairline border-b">
                  {firstSelected.columns.map((col) => (
                    <th
                      key={col.name}
                      scope="col"
                      className="px-2 py-1 text-left font-medium text-ink-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-ink-1">{col.original_name}</span>
                        <span className="font-normal text-[9.5px] text-ink-3">{col.type}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {firstSelected.preview_rows.map((row, rowIdx) => {
                  // Stable key derived from the row contents — the
                  // preview is immutable for the lifetime of Step 2,
                  // so we serialize each row to a deterministic key.
                  // Falls back to the row index if serialization fails
                  // (circular refs, BigInt, …).
                  const rowKey = stableKey(row, `row-${rowIdx}`);
                  return (
                    <tr key={rowKey} className="border-hairline/60 border-b last:border-b-0">
                      {row.map((cell, cellIdx) => {
                        const colName = firstSelected.columns[cellIdx]?.name ?? `c${cellIdx}`;
                        return (
                          <td
                            key={`${rowKey}-${colName}`}
                            className="max-w-[180px] truncate px-2 py-1 text-ink-2"
                          >
                            {renderCell(cell)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}

      {/* Connector name */}
      <div className="flex flex-col gap-1">
        <label
          htmlFor="spreadsheet-connector-name"
          className="font-medium text-[11.5px] text-ink-2"
        >
          <Trans>Nome del connettore</Trans>
          <span className="ml-0.5 text-error" aria-hidden>
            *
          </span>
        </label>
        <input
          id="spreadsheet-connector-name"
          name="connector_name"
          type="text"
          required
          disabled={submitting}
          placeholder={t`Es. Fatture Q1 2026`}
          value={connectorName}
          onChange={(e) => onConnectorNameChange(e.target.value)}
          className={cn(
            "h-9 rounded-md border border-hairline bg-white/80 px-2.5 text-[13px] text-ink-1 placeholder:text-ink-3",
            "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
            "disabled:opacity-60",
          )}
        />
      </div>

      <InlineErrorBanner message={error} />

      {/* Chunked-upload progress bar — visibile mentre il file
          > 25 MB sta viaggiando a chunk verso MinIO (S3 multipart
          via BE proxy). Una sola riga: % + Annulla. */}
      {uploadProgress ? (
        <output
          aria-live="polite"
          className="block rounded-md border border-hairline bg-cream/40 px-3 py-2.5"
        >
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11.5px] text-ink-2">
              <Trans>
                Caricamento file ·{" "}
                {uploadProgress.percent.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%
              </Trans>
              <span className="ml-1.5 text-ink-3">
                ({formatBytes(uploadProgress.loaded)} / {formatBytes(uploadProgress.total)})
              </span>
            </span>
            {uploadProgress.percent < 100 ? (
              <button
                type="button"
                onClick={onCancelUpload}
                className="font-medium text-[11.5px] text-ink-3 transition-colors hover:text-ando-orange focus-visible:underline focus-visible:outline-none"
              >
                <Trans>Annulla</Trans>
              </button>
            ) : null}
          </div>
          <div
            aria-hidden
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-1/[0.06]"
          >
            <div
              className="h-full rounded-full bg-ando-orange transition-[width] duration-200"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
        </output>
      ) : null}

      <div className="mt-1 flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="md" onClick={onBack} disabled={submitting}>
          <Trans>Indietro</Trans>
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={submitting || selectedSheets.length === 0}
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {uploadProgress ? t`Caricamento…` : t`Importazione in corso…`}
            </>
          ) : (
            <>
              <Trans>Importa</Trans>
              <ArrowRight aria-hidden />
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

SpreadsheetPreviewStep.displayName = "SpreadsheetPreviewStep";
